import PropTypes from 'prop-types';
import { Fragment, useEffect } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
// @mui
import { styled, keyframes } from '@mui/material/styles';
import { Box, Link, Stack, Typography } from '@mui/material';
// components
import Iconify from '../iconify';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getAnnouncements } from '../../redux/slices/course';

// ----------------------------------------------------------------------

// Height of the ribbon strip. Layouts use this to offset the fixed navbar and
// the page content so the ribbon never overlaps them.
export const NAV_RIBBON_HEIGHT = 40;

// The track holds two identical copies of the items, so translating it by half
// of its own width loops the marquee seamlessly.
const marqueeKeyframes = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const StyledRoot = styled(Box)(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  height: NAV_RIBBON_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  position: 'fixed',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  color: theme.palette.common.white,
  backgroundColor: '#0288D1',
  '&:hover .nav-ribbon-track': {
    animationPlayState: 'paused',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& .nav-ribbon-track': {
      animation: 'none',
    },
  },
}));

const StyledBadge = styled(Stack)(({ theme }) => ({
  height: '100%',
  flexShrink: 0,
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  backgroundColor: '#DF3E30',
}));

const StyledMarquee = styled(Box)({
  flex: '1 1 auto',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 48px, #000 calc(100% - 48px), transparent 100%)',
  maskImage: 'linear-gradient(90deg, transparent 0, #000 48px, #000 calc(100% - 48px), transparent 100%)',
});

const StyledTrack = styled('div', { shouldForwardProp: (prop) => prop !== 'speed' })(({ speed }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'max-content',
  whiteSpace: 'nowrap',
  animation: `${marqueeKeyframes} ${speed}s linear infinite`,
}));

const StyledItem = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0, 3),
  color: 'inherit',
}));

const StyledItemLink = styled(Link)(({ theme }) => ({
  ...theme.typography.body2,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0, 3),
  color: 'inherit',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}));

// ----------------------------------------------------------------------

RibbonItem.propTypes = {
  item: PropTypes.object,
};

function RibbonItem({ item }) {
  const content = (
    <>
      {item.icon && <Iconify icon={item.icon} width={16} />}
      <Typography variant="body2" component="span" sx={{ color: 'inherit' }}>
        {item.text}
      </Typography>
    </>
  );

  if (item.path) {
    return (
      <StyledItemLink component={RouterLink} to={item.path}>
        {content}
      </StyledItemLink>
    );
  }

  return <StyledItem>{content}</StyledItem>;
}

// ----------------------------------------------------------------------

NavRibbon.propTypes = {
  data: PropTypes.array,
  speed: PropTypes.number,
  label: PropTypes.string,
  sx: PropTypes.object,
};

export default function NavRibbon({ data, speed = 32, label = 'Latest', sx, ...other }) {
  const dispatch = useDispatch();
  const announcements = useSelector((state) => state.course.announcements);

  useEffect(() => {
    dispatch(getAnnouncements());
  }, [dispatch]);

  // A caller-supplied `data` always wins, so the ribbon stays reusable for a
  // static or filtered set. The slice already falls back to the mock items
  // when the endpoint is unavailable, so an empty store simply renders nothing.
  const source = data ?? announcements;
  const items = Array.isArray(source) ? source.filter((item) => item && item.text) : [];

  if (!items.length) {
    return null;
  }

  // Duplicate the sequence so the marquee can loop without a visible jump.
  const looped = [...items, ...items];

  return (
    <StyledRoot sx={sx} {...other}>
      <StyledBadge direction="row" spacing={0.5}>
        <Iconify icon="mdi:bullhorn" width={16} />
        <Typography
          variant="subtitle2"
          sx={{ color: 'common.white', textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
      </StyledBadge>

      <StyledMarquee>
        <StyledTrack className="nav-ribbon-track" speed={speed}>
          {looped.map((item, index) => (
            <Fragment key={`${item.id ?? item.text}-${index}`}>
              <RibbonItem item={item} />
              <Box
                component="span"
                sx={{ flexShrink: 0, fontSize: 18, lineHeight: 1, opacity: 0.48, color: 'inherit' }}
              >
                &bull;
              </Box>
            </Fragment>
          ))}
        </StyledTrack>
      </StyledMarquee>
    </StyledRoot>
  );
}
